import React, { useEffect, useState } from 'react'
import { useRouteMatch, Switch, Route, Redirect } from 'react-router-dom'
import Button from '@material-ui/core/Button'
import AddIcon from 'components/material-symbol/icons/Add'
import FileDownloadOutlinedIcon from 'components/material-symbol/icons/FileDownload'
import ErrorView from 'components/error'
import { PageHeader } from 'components/page-header'
import ApolloDataView from 'components/apollo-data-view'
import CoachAddStudentModal from 'components/add-student-modal/coach-add-student-modal'
import ExportStudentListDialog from './export-student-list-dialog'
import StudentListView from './student-list-new-view'
import LoadingView from './loading-view'
import { DateTime } from 'luxon'
import { useGetStudents, useGetUpcomingSessions } from './api-hooks'
import {
  flags,
  useFeatureFlags,
  useFeatureFlagsLoading,
} from 'lib/feature-flags'
import { isInsightsOnlyError } from './insights-utils'
import FollowUpsPage from './follow-ups'

const StudentsListPage = React.memo(() => {
  const [openStudentDialog, setOpenStudentDialog] = useState(false)
  const [openExportDialog, setOpenExportDialog] = useState(false)
  const { path } = useRouteMatch()
  const [isStudentInsightsEnabled] = useFeatureFlags([
    flags.FLAG_FEAT_STUDENT_INSIGHTS,
  ])
  // The first flag evaluation is anonymous, so wait for the signed-in one
  // before asking for insights; otherwise the list loads, then reloads.
  const flagsLoading = useFeatureFlagsLoading()
  const { loading, error, data, refetch } = useGetStudents({
    withInsights: isStudentInsightsEnabled,
    skip: flagsLoading,
  })
  // An insights failure hides the engagement status column instead of the list,
  // so a missing insight is never shown as "No lessons yet".
  const insightsFailed = isInsightsOnlyError(error)
  const studentsError = insightsFailed ? undefined : error
  const showInsights = isStudentInsightsEnabled && !insightsFailed

  useEffect(() => {
    if (insightsFailed) {
      window.rg4js &&
        window.rg4js('send', { error, tags: ['student-insights'] })
    }
  }, [insightsFailed, error])

  const upcomingBookingsDateRange = {
    from: DateTime.now()
      .startOf('day')
      .toISO(),
    to: null,
  }

  const {
    loading: sessionsLoading,
    error: sessionsError,
    data: sessionsData,
    refetch: sessionsRefetch,
  } = useGetUpcomingSessions(upcomingBookingsDateRange, 'ASC')

  const pageLoading = loading || sessionsLoading || flagsLoading

  const actions = (
    <>
      <Button
        variant="outlined"
        color="primary"
        size="large"
        startIcon={<FileDownloadOutlinedIcon />}
        onClick={() => setOpenExportDialog(true)}
        disabled={pageLoading || !data?.coachStudents?.length}
      >
        Export List
      </Button>
      <Button
        variant="contained"
        color="primary"
        size="large"
        startIcon={<AddIcon />}
        onClick={() => setOpenStudentDialog(!openStudentDialog)}
      >
        Create Student
      </Button>
    </>
  )

  return (
    <>
      <PageHeader
        title="Students"
        actions={actions}
        fixedMobileActions
        keepActionsInline
      />
      <Switch>
        <Route exact path={`${path}`}>
          <Redirect to={`${path}/list`} />
        </Route>
        <Route
          path={`${path}/list`}
          exact
          render={() => (
            <ApolloDataView
              error={studentsError || sessionsError}
              loading={pageLoading}
              data={data?.coachStudents}
            >
              {{
                errorView: <ErrorView />,
                emptyView: (
                  <StudentListView
                    students={[]}
                    sessions={sessionsData}
                    refetchStudents={refetch}
                    refetchSessions={sessionsRefetch}
                    showInsights={showInsights}
                  />
                ),
                loadingView: <LoadingView />,
                dataView: students => (
                  <StudentListView
                    students={students}
                    sessions={sessionsData}
                    refetchStudents={refetch}
                    refetchSessions={sessionsRefetch}
                    showInsights={showInsights}
                  />
                ),
              }}
            </ApolloDataView>
          )}
        />
      </Switch>
      <CoachAddStudentModal
        {...{
          open: openStudentDialog,
          setOpen: setOpenStudentDialog,
          onAdd: refetch,
        }}
      />
      <ExportStudentListDialog
        open={openExportDialog}
        onClose={() => setOpenExportDialog(false)}
        students={data?.coachStudents}
      />
    </>
  )
})

// The Follow-ups page has its own header, and must not load the students list.
const StudentsPage = () => {
  const { path } = useRouteMatch()

  return (
    <Switch>
      <Route path={`${path}/follow-ups`} exact>
        <FollowUpsPage />
      </Route>
      <Route>
        <StudentsListPage />
      </Route>
    </Switch>
  )
}

export default StudentsPage
